import axios from "axios";

/**
 * PayFast checkout is initiated by the backend (POST /payments/checkout):
 * amounts, credentials and the MD5 signature are resolved server-side and
 * returned as a signed, ORDERED field list. This module only renders those
 * fields as a hidden form and submits it — PayFast's redirect checkout
 * requires a browser form POST.
 */

export type PayfastCheckoutIntent =
  | "subscribe"
  | "billImmediately"
  | "reactivate"
  | "digitalFlyer";

export interface PayfastCheckoutOptions {
  merchantId: number;
  intent: PayfastCheckoutIntent;
  referralCode?: string;
  flyerId?: number;
}

interface PayfastCheckoutResponse {
  action_url: string;
  fields: { name: string; value: string }[];
}

// Prevent duplicate submissions from double-clicks / rapid retries.
const activeSubmissions = new Set<string>();

export const initiatePayfastCheckout = async (
  options: PayfastCheckoutOptions
): Promise<void> => {
  const submissionKey = `${options.merchantId}:${options.intent}:${options.flyerId ?? ""}`;
  if (activeSubmissions.has(submissionKey)) {
    console.warn("Duplicate PayFast submission blocked:", submissionKey);
    return;
  }
  activeSubmissions.add(submissionKey);
  setTimeout(() => activeSubmissions.delete(submissionKey), 30000);

  let checkout: PayfastCheckoutResponse;
  try {
    const response = await axios.post(
      `${import.meta.env.REACT_APP_API_URL}payments/checkout`,
      options
    );
    checkout = response.data;
  } catch (error) {
    activeSubmissions.delete(submissionKey);
    console.error("Failed to initiate PayFast checkout:", error);
    throw error;
  }

  const form = document.createElement("form");
  form.setAttribute("method", "post");
  form.setAttribute("action", checkout.action_url);

  // Field order carries the signature — render in exactly the order received.
  for (const field of checkout.fields) {
    const hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", field.name);
    hiddenField.setAttribute("value", field.value);
    form.appendChild(hiddenField);
  }

  document.body.appendChild(form);
  form.submit();
};

/** PayFast card-update URL for the merchant's subscription (backend-built). */
export const getUpdatePaymentDetailsUrl = async (
  merchantId: number
): Promise<string> => {
  const response = await axios.get(
    `${import.meta.env.REACT_APP_API_URL}payments/update-details-url/${merchantId}`
  );
  return response.data.url;
};
